import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import {
  BankAccountResponse,
  BankAccount,
  BankAccountAddEdit,
  BankAccountOfficeProfileRequest,
} from "@/models/api/administration/bankAccounts/BankAccountsList";
import { BankStatementParser } from "@/models/api/administration/bankAccounts/BankStatementParser";
import { InformationAboutBankAccount } from "@/models/api/administration/bankAccounts/InformationAboutBankAccount";

export const URL_GET_BANK_ACCOUNT_PAGED = "/BankAccount/Paged";
export const URL_GET_BANK_ACCOUNT_BY_ID = "/BankAccount/${BankAccountId}";
export const URL_GET_BANK_NAMES = "/BankAccount/BankNames";
export const URL_GET_BANK_STATEMENT_PARSERS = "/BankAccount/BankStatementParsers";
export const URL_GET_INFORMATION_ABOUT_BANK_ACCOUNT = "/BankAccount/${BankAccountId}/Information";
export const URL_GET_BANK_ACCOUNT_OFFICE_PROFILES_PAGED =
  "/BankAccount/${OfficeProfileId}/BankAccountOfficeProfile/Paged";

export const URL_POST_REMOVE_BANK_ACCOUNT = "/BankAccount/${BankAccountId}/Remove";
export const URL_POST_LOCK_BANK_ACCOUNT = "/BankAccount/${BankAccountId}/Lock";
export const URL_POST_UNLOCK_BANK_ACCOUNT = "/BankAccount/${BankAccountId}/Unlock";
export const URL_POST_UPDATE_BANK_ACCOUNT = "/BankAccount/Update";
export const URL_POST_ADD_NEW_BANK_ACCOUNT = "/BankAccount/Create";

export const URL_POST_ASSIGN = "/BankAccount/AssignedToOfficeProfile";
export const URL_POST_DISCONNECT = "/BankAccount/DisconnectToOfficeProfile";
export const URL_POST_SET_DEFAULT = "/BankAccount/SetDefaultToOfficeProfile";
export const URL_POST_UNSET_DEFAULT = "/BankAccount/UnsetDefaultToOfficeProfile";

export const URL_GET_ALL_BANK_ACCOUNTS = "/BankAccount/All";
export const URL_GET_OFFICE_PROFILE_BANK_ACCOUNT_IS_ASSIGNED = "/BankAccount/OfficeProfileBankAccountIsAssigned";

export const URL_POST_ASSIGN_FACTOR = "/BankAccount/${BankAccountId}/Assign/Contractor/${ContractorId}";
export const URL_POST_REMOVE_FACTOR = "/BankAccount/${BankAccountId}/Remove/Contractor";

const _getBankAccountList = new CancellableHelper<BankAccountResponse>(URL_GET_BANK_ACCOUNT_PAGED, BankAccountResponse);
_getBankAccountList.jsonSerializer = true;
export const getBankAccountList = async (genericTableRequest: GenericTableRequest): Promise<BankAccountResponse> => {
  return await _getBankAccountList.get(genericTableRequest);
};

const _getBankAccountById = new CancellableHelper<BankAccount>(URL_GET_BANK_ACCOUNT_BY_ID);
export const getBankAccountById = async (bankAccountId: number): Promise<BankAccount> => {
  return await _getBankAccountById.get({ BankAccountId: bankAccountId });
};

const _getBankNames = new CancellableHelper<Array<string>>(URL_GET_BANK_NAMES);
export const getBankNames = async (): Promise<Array<string>> => {
  return await _getBankNames.get();
};

const _getBankStatementParsers = new CancellableHelper<Array<BankStatementParser>>(
  URL_GET_BANK_STATEMENT_PARSERS,
  BankStatementParser
);
export const getBankStatementParsers = async (): Promise<Array<BankStatementParser>> => {
  return await _getBankStatementParsers.get();
};

const _getBankAccountAssign = new CancellableHelper<BankAccountResponse>(
  URL_GET_BANK_ACCOUNT_OFFICE_PROFILES_PAGED,
  BankAccountResponse
);
_getBankAccountAssign.jsonSerializer = true;
export const getBankAccountAssign = async (
  officeProfileId: number,
  genericTableRequest: GenericTableRequest
): Promise<BankAccountResponse> => {
  return await _getBankAccountAssign.get({
    OfficeProfileId: officeProfileId,
    ...genericTableRequest,
  });
};

const _getInformationAboutBankAccount = new CancellableHelper<InformationAboutBankAccount>(
  URL_GET_INFORMATION_ABOUT_BANK_ACCOUNT,
  InformationAboutBankAccount
);
export const getInformationAboutBankAccount = async (bankAccountId: number): Promise<InformationAboutBankAccount> => {
  return await _getInformationAboutBankAccount.get({
    BankAccountId: bankAccountId,
  });
};

export const removeBankAccount = async (bankAccountId: number): Promise<null> => {
  const _remove = new CancellableHelper<null>(URL_POST_REMOVE_BANK_ACCOUNT, null, true);
  return await _remove.post({ BankAccountId: bankAccountId }, null);
};

export const lockBankAccount = async (bankAccountId: number): Promise<null> => {
  const _lock = new CancellableHelper<null>(URL_POST_LOCK_BANK_ACCOUNT, null, true);
  return await _lock.post({ BankAccountId: bankAccountId }, null);
};

export const unlockBankAccount = async (bankAccountId: number): Promise<null> => {
  const _unlock = new CancellableHelper<null>(URL_POST_UNLOCK_BANK_ACCOUNT, null, true);
  return await _unlock.post({ BankAccountId: bankAccountId }, null);
};

export const updateBankAccount = async (bankAccount: BankAccountAddEdit): Promise<null> => {
  const _update = new CancellableHelper<null>(URL_POST_UPDATE_BANK_ACCOUNT, null, true);
  return await _update.post(null, bankAccount);
};

export const addNewBankAccount = async (newBankAccount: BankAccountAddEdit): Promise<null> => {
  const _addNew = new CancellableHelper<null>(URL_POST_ADD_NEW_BANK_ACCOUNT, null, true);
  return await _addNew.post(null, newBankAccount);
};

export const assignBankAccountToOfficeProfile = async (
  bankAccountOfficeProfile: BankAccountOfficeProfileRequest
): Promise<null> => {
  const _assign = new CancellableHelper<null>(URL_POST_ASSIGN, null, true);
  return await _assign.post(null, bankAccountOfficeProfile);
};

export const disconnectBankAccountToOfficeProfile = async (
  bankAccountOfficeProfile: BankAccountOfficeProfileRequest
): Promise<null> => {
  const _disconnect = new CancellableHelper<null>(URL_POST_DISCONNECT, null, true);
  return await _disconnect.post(null, bankAccountOfficeProfile);
};

export const setDefaultBankAccountToOfficeProfile = async (
  bankAccountOfficeProfile: BankAccountOfficeProfileRequest
): Promise<null> => {
  const _setDefault = new CancellableHelper<null>(URL_POST_SET_DEFAULT, null, true);
  return await _setDefault.post(null, bankAccountOfficeProfile);
};

export const unsetDefaultBankAccountToOfficeProfile = async (
  bankAccountOfficeProfile: BankAccountOfficeProfileRequest
): Promise<null> => {
  const _unsetDefault = new CancellableHelper<null>(URL_POST_UNSET_DEFAULT, null, true);
  return await _unsetDefault.post(null, bankAccountOfficeProfile);
};

const _getAllBankAccounts = new CancellableHelper<Array<BankAccount>>(URL_GET_ALL_BANK_ACCOUNTS, BankAccount);
export const getAllBankAccounts = async (): Promise<Array<BankAccount>> => {
  return await _getAllBankAccounts.get();
};

const _containsContractor = new CancellableHelper<boolean>(URL_GET_OFFICE_PROFILE_BANK_ACCOUNT_IS_ASSIGNED);
export const containsContractor = async (bankAccountId: number): Promise<boolean> => {
  return _containsContractor.get({ BankAccountId: bankAccountId });
};

const _assignBankAccountToFactor = new CancellableHelper<null>(URL_POST_ASSIGN_FACTOR);
export const assignBankAccountFactor = async (bankAccountId: number, contractorId: string): Promise<null> => {
  return _assignBankAccountToFactor.post({ BankAccountId: bankAccountId, ContractorId: contractorId });
};

const _removeBankAccountFromFactor = new CancellableHelper<null>(URL_POST_REMOVE_FACTOR);
export const removeBankAccountFactor = async (bankAccountId: number): Promise<null> => {
  return _removeBankAccountFromFactor.post({ BankAccountId: bankAccountId });
};
